// ============================================================================
// contexts/liveStreamManagment/AudioMixerContext.tsx
// Provider React pour le AudioMixerPipeline.
//
// REFONTE v3 — Simplifié et centralisé.
//
// CORRECTIF v3.1 — connectAudioForSource, cas 'device'.
//
// v3.3 — Expose connectMonitorOutput / disconnectMonitorOutput depuis
//   AudioMixerPipeline pour que initAudioMixer puisse contrôler
//   explicitement quelle source est audible localement, de façon
//   symétrique au contrôle du busDest (WHIP).
// ============================================================================

import React, {
  createContext, useContext, useEffect, useRef, useState,
  useCallback, useMemo,
  ReactNode,
} from 'react';

import {
  AudioMixerPipeline,
  AudioInput,
  AudioInputConfig,
  AudioLevel,
  AudioMixerEvent,
  AudioEffectFactory,
  SILENT_LEVEL,
} from '../../dataTypes/liveStreamManagment/audioPipeline';

import { AudioSourceRegistry, AudioSourceAnalysers } from '../../dataTypes/liveStreamManagment/audioPipeline/AudioSourceRegistry';

// ─── Interface du contexte ────────────────────────────────────────────────────

interface AudioMixerContextType {
  mixer: AudioMixerPipeline;
  audioSourceRegistry: AudioSourceRegistry;

  connectMicrophone:   (config: AudioInputConfig, deviceId?: string) => Promise<AudioInput>;
  connectStream:       (config: AudioInputConfig, stream: MediaStream, owned?: boolean) => AudioInput;
  connectMediaElement: (config: AudioInputConfig, element: HTMLVideoElement | HTMLAudioElement) => AudioInput;
  connectBuffer:       (config: AudioInputConfig, buffer: AudioBuffer) => Promise<AudioInput>;
  disconnectInput:     (inputId: string) => void;

  connectAudioForSource: (
    sourceId: string,
    sourceType: 'device' | 'remote' | 'screen' | 'url' | 'scene',
    stream: MediaStream | null,
    audioDeviceId?: string,
  ) => Promise<void>;

  disconnectAudioForSource: (sourceId: string) => void;

  getSourceAnalysers: (sourceId: string) => AudioSourceAnalysers | null;
  /** Analyseurs du signal pondéré K, pour les VU en LUFS (créés au premier appel). */
  getSourceLoudnessAnalysers: (sourceId: string) => AudioSourceAnalysers | null;
  hasAudioSource: (sourceId: string) => boolean;

  // ── v3.3 : contrôle explicite du monitoring local ─────────────────────────
  connectMonitorOutput:    (id: string, monitorNode: AudioNode) => void;
  disconnectMonitorOutput: (id: string, monitorNode: AudioNode) => void;

  setVolume:       (inputId: string, volume: number) => void;
  setMuted:        (inputId: string, muted: boolean) => void;
  setStereoVolume: (inputId: string, left: number, right: number) => void;
  setMonitor:      (inputId: string, monitor: boolean) => void;

  registerEffect: (id: string, factory: AudioEffectFactory) => void;
  addEffect:      (inputId: string, effectId: string) => void;
  removeEffect:   (inputId: string, effectId: string) => void;
  toggleEffect:   (inputId: string, effectId: string, enabled?: boolean) => void;

  getLevel:  (inputId: string) => AudioLevel;
  onLevel:   (inputId: string, callback: (level: AudioLevel) => void) => void;
  offLevel:  (inputId: string) => void;

  getInput:     (inputId: string) => AudioInput | undefined;
  getAllInputs:  () => AudioInput[];
  isConnected:  (inputId: string) => boolean;

  decodeAudioUrl: (url: string) => Promise<AudioBuffer>;
  outputStream:   MediaStream | null;
}

// ─── Contexte ─────────────────────────────────────────────────────────────────

const AudioMixerContext = createContext<AudioMixerContextType | undefined>(undefined);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const AudioMixerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {

  const { current: mixer } = useRef(new AudioMixerPipeline());
  const { current: audioSourceRegistry } = useRef(new AudioSourceRegistry());

  const [, setVersion] = useState(0);
  const bump = useCallback(() => setVersion(v => v + 1), []);

  const audioRegistryIdsRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    mixer.init();
    mixer.setSourceRegistry(audioSourceRegistry);
    const unsub = mixer.subscribe((_event: AudioMixerEvent) => {
      if (_event.type === 'input-connected' || _event.type === 'input-disconnected') {
        bump();
      }
    });
    return () => {
      unsub();
      mixer.destroy();
      audioSourceRegistry.destroy();
    };
  }, [mixer, audioSourceRegistry, bump]);

  // ── Wrappers stables ──────────────────────────────────────────────────────

  const connectMicrophone = useCallback(
    (config: AudioInputConfig, deviceId?: string) => mixer.connectMicrophone(config, deviceId),
    [mixer],
  );

  const connectStream = useCallback(
    (config: AudioInputConfig, stream: MediaStream, owned?: boolean) => mixer.connectStream(config, stream, owned),
    [mixer],
  );

  const connectMediaElement = useCallback(
    (config: AudioInputConfig, element: HTMLVideoElement | HTMLAudioElement) => mixer.connectMediaElement(config, element),
    [mixer],
  );

  const connectBuffer = useCallback(
    (config: AudioInputConfig, buffer: AudioBuffer) => mixer.connectBuffer(config, buffer),
    [mixer],
  );

  const disconnectInput = useCallback(
    (inputId: string) => mixer.disconnectInput(inputId),
    [mixer],
  );

  // ── Connexion audio centralisée ───────────────────────────────────────────

  const connectAudioForSource = useCallback(async (
    sourceId: string,
    sourceType: 'device' | 'remote' | 'screen' | 'url' | 'scene',
    stream: MediaStream | null,
    audioDeviceId?: string,
  ): Promise<void> => {
	  
console.log('[MIXER] connectAudioForSource ENTRÉE', sourceId, 
  'type=', sourceType,
  'stream=', stream?.id ?? 'null',
  'audioTracks=', stream?.getAudioTracks().length ?? 0,
  'alreadyInRegistry=', audioSourceRegistry.has(`audio-${sourceId}`));	  
	  
	  
    const audioRegId = `audio-${sourceId}`;
    if (audioSourceRegistry.has(audioRegId)) {
      console.log(`[AudioMixerContext] connectAudioForSource(${sourceId}) — déjà connecté, skip`);
      return;
    }

    console.log(`[AudioMixerContext] connectAudioForSource(${sourceId}) — type=${sourceType}, hasStream=${!!stream}, audioTracks=${stream?.getAudioTracks().length ?? 0}, audioDeviceId=${audioDeviceId ?? 'none'}`);

    if (sourceType === 'scene') {
      console.log(`[AudioMixerContext] connectAudioForSource(${sourceId}) — type scene, skip audio`);
      return;
    }

    try {
      if (sourceType === 'device') {
        if (stream && stream.getAudioTracks().length > 0) {
          audioSourceRegistry.borrowFromStream(audioRegId, stream, 'stream', false);
          audioRegistryIdsRef.current.set(sourceId, audioRegId);
          console.log(`[AudioMixerContext] connectAudioForSource(${sourceId}) — device : piste audio du stream caméra connectée au registry`);
        } else if (audioDeviceId) {
          await audioSourceRegistry.borrowFromMicrophone(audioRegId, audioDeviceId);
          audioRegistryIdsRef.current.set(sourceId, audioRegId);
          console.log(`[AudioMixerContext] connectAudioForSource(${sourceId}) — device : micro dédié connecté au registry (deviceId=${audioDeviceId})`);
        } else {
          console.log(`[AudioMixerContext] connectAudioForSource(${sourceId}) — device : pas de piste audio disponible et pas de deviceId, skip`);
          return;
        }
      } else if (stream && stream.getAudioTracks().length > 0) {
        const useAudioEl = sourceType === 'remote';
        audioSourceRegistry.borrowFromStream(audioRegId, stream, sourceType, useAudioEl);
        audioRegistryIdsRef.current.set(sourceId, audioRegId);
        console.log(`[AudioMixerContext] connectAudioForSource(${sourceId}) — stream audio connecté au registry (useAudioEl=${useAudioEl})`);
      } else {
        console.log(`[AudioMixerContext] connectAudioForSource(${sourceId}) — pas de piste audio disponible`);
        return;
      }
    } catch (e) {
      console.warn(`[AudioMixerContext] connectAudioForSource(${sourceId}) — ERREUR:`, e);
    }
  }, [audioSourceRegistry]);

  const disconnectAudioForSource = useCallback((sourceId: string) => {
    const audioRegId = audioRegistryIdsRef.current.get(sourceId);
    if (!audioRegId) {
      console.log(`[AudioMixerContext] disconnectAudioForSource(${sourceId}) — pas de registry ID, skip`);
      return;
    }

    console.log(`[AudioMixerContext] disconnectAudioForSource(${sourceId}) — libération registry ID=${audioRegId}`);

    audioSourceRegistry.release(audioRegId);

    if (mixer.isConnected(audioRegId)) {
      mixer.disconnectInput(audioRegId);
    }

    audioRegistryIdsRef.current.delete(sourceId);
  }, [audioSourceRegistry, mixer]);

  const getSourceAnalysers = useCallback((sourceId: string): AudioSourceAnalysers | null => {
    const audioRegId = audioRegistryIdsRef.current.get(sourceId) ?? `audio-${sourceId}`;
    return audioSourceRegistry.getAnalysers(audioRegId);
  }, [audioSourceRegistry]);

  const getSourceLoudnessAnalysers = useCallback((sourceId: string): AudioSourceAnalysers | null => {
    const audioRegId = audioRegistryIdsRef.current.get(sourceId) ?? `audio-${sourceId}`;
    return audioSourceRegistry.getLoudnessAnalysers(audioRegId);
  }, [audioSourceRegistry]);

  const hasAudioSource = useCallback((sourceId: string): boolean => {
    const audioRegId = audioRegistryIdsRef.current.get(sourceId) ?? `audio-${sourceId}`;
    return audioSourceRegistry.has(audioRegId);
  }, [audioSourceRegistry]);

  // ── v3.3 : monitoring local ───────────────────────────────────────────────

  const connectMonitorOutput = useCallback(
    (id: string, monitorNode: AudioNode) => mixer.connectMonitorOutput(id, monitorNode),
    [mixer],
  );

  const disconnectMonitorOutput = useCallback(
    (id: string, monitorNode: AudioNode) => mixer.disconnectMonitorOutput(id, monitorNode),
    [mixer],
  );

  // ── Contrôle ──────────────────────────────────────────────────────────────

  const setVolume = useCallback((inputId: string, volume: number) => mixer.setVolume(inputId, volume), [mixer]);
  const setMuted = useCallback((inputId: string, muted: boolean) => mixer.setMuted(inputId, muted), [mixer]);
  const setStereoVolume = useCallback((inputId: string, left: number, right: number) => mixer.setStereoVolume(inputId, left, right), [mixer]);
  const setMonitor = useCallback((inputId: string, monitor: boolean) => mixer.setMonitor(inputId, monitor), [mixer]);

  const registerEffect = useCallback((id: string, factory: AudioEffectFactory) => mixer.registerEffect(id, factory), [mixer]);
  const addEffect = useCallback((inputId: string, effectId: string) => { mixer.addEffect(inputId, effectId); }, [mixer]);
  const removeEffect = useCallback((inputId: string, effectId: string) => mixer.removeEffect(inputId, effectId), [mixer]);
  const toggleEffect = useCallback((inputId: string, effectId: string, enabled?: boolean) => mixer.toggleEffect(inputId, effectId, enabled), [mixer]);

  const getLevel = useCallback((inputId: string) => mixer.getLevel(inputId), [mixer]);
  const onLevel = useCallback((inputId: string, cb: (level: AudioLevel) => void) => mixer.onLevel(inputId, cb), [mixer]);
  const offLevel = useCallback((inputId: string) => mixer.offLevel(inputId), [mixer]);

  const getInput = useCallback((inputId: string) => mixer.getInput(inputId), [mixer]);
  const getAllInputs = useCallback(() => mixer.getAllInputs(), [mixer]);
  const isConnected = useCallback((inputId: string) => mixer.isConnected(inputId), [mixer]);

  const decodeAudioUrl = useCallback((url: string) => mixer.decodeAudioUrl(url), [mixer]);

  const outputStream = useMemo(() => mixer.getOutputStream(), [mixer]);

  // ── Valeur du contexte ────────────────────────────────────────────────────

  const value = useMemo<AudioMixerContextType>(() => ({
    mixer,
    audioSourceRegistry,
    connectMicrophone, connectStream, connectMediaElement, connectBuffer, disconnectInput,
    connectAudioForSource, disconnectAudioForSource,
    getSourceAnalysers, getSourceLoudnessAnalysers, hasAudioSource,
    connectMonitorOutput, disconnectMonitorOutput,
    setVolume, setMuted, setStereoVolume, setMonitor,
    registerEffect, addEffect, removeEffect, toggleEffect,
    getLevel, onLevel, offLevel,
    getInput, getAllInputs, isConnected,
    decodeAudioUrl, outputStream,
  }), [
    mixer, audioSourceRegistry,
    connectMicrophone, connectStream, connectMediaElement, connectBuffer, disconnectInput,
    connectAudioForSource, disconnectAudioForSource,
    getSourceAnalysers, getSourceLoudnessAnalysers, hasAudioSource,
    connectMonitorOutput, disconnectMonitorOutput,
    setVolume, setMuted, setStereoVolume, setMonitor,
    registerEffect, addEffect, removeEffect, toggleEffect,
    getLevel, onLevel, offLevel,
    getInput, getAllInputs, isConnected,
    decodeAudioUrl, outputStream,
  ]);

  return (
    <AudioMixerContext.Provider value={value}>
      {children}
    </AudioMixerContext.Provider>
  );
};

// ─── Hook consommateur principal ──────────────────────────────────────────────

export function useAudioMixer(): AudioMixerContextType {
  const ctx = useContext(AudioMixerContext);
  if (!ctx) {
    throw new Error('useAudioMixer must be used within AudioMixerProvider');
  }
  return ctx;
}

// ─── Hook utilitaire : niveau audio réactif pour une source ───────────────────

export function useAudioLevel(inputId: string | null): AudioLevel {
  const { onLevel, offLevel, getLevel } = useAudioMixer();
  const [level, setLevel] = useState<AudioLevel>(SILENT_LEVEL);

  useEffect(() => {
    if (!inputId) {
      setLevel(SILENT_LEVEL);
      return;
    }

    setLevel(getLevel(inputId));

    onLevel(inputId, (newLevel) => {
      setLevel(prev => {
        if (Math.abs(prev.left - newLevel.left) < 1 && Math.abs(prev.right - newLevel.right) < 1) {
          return prev;
        }
        return newLevel;
      });
    });

    return () => {
      offLevel(inputId);
    };
  }, [inputId, onLevel, offLevel, getLevel]);

  return level;
}